import { ReactNode } from 'react';

import { Box, Tooltip, TooltipProps, Typography } from '@mui/material';

import { OnboardingStatusMap } from '../views/FirmBankingView/constants';
import { ClientOnboardingStatusItem } from '../types';
import { getOnboardingStatusIcon } from './OnboardingStatusIcon';

interface OnboardingStatusStackProps {
  /** Latest onboarding status of the client. */
  status?: string;
  /** All onboardings of the client, newest first. */
  items?: ClientOnboardingStatusItem[];
  fontSize?: number;
  /** Max icons shown; the rest collapse into a count badge. Omit to show all. */
  maxVisible?: number;
}

const getMappedStatus = (status?: string) =>
  OnboardingStatusMap[(status || 'IN_PROGRESS') as keyof typeof OnboardingStatusMap];

const tooltipSlotProps: TooltipProps['slotProps'] = {
  tooltip: {
    sx: (theme) => ({
      backgroundColor: theme.palette.common.white,
      color: theme.palette.primary.light,
      borderRadius: '5px',
      boxShadow: 3,
      fontFamily: 'Inter',
      fontSize: '13px',
      fontWeight: 500,
      padding: '6px 8px',
      maxWidth: 'min(320px, 90vw)',
    }),
  },
  arrow: {
    sx: (theme) => ({
      color: theme.palette.common.white,
      '&:before': {
        boxShadow: 3,
      },
    }),
  },
};

const StatusTooltip = ({ title, children }: { title: ReactNode; children: TooltipProps['children'] }) => (
  <Tooltip
    arrow
    title={title}
    enterTouchDelay={0}
    leaveTouchDelay={3000}
    slotProps={tooltipSlotProps}
  >
    {children}
  </Tooltip>
);

const OnboardingStatusTooltipRow = ({ item }: { item: ClientOnboardingStatusItem }) => {
  const mappedStatus = getMappedStatus(item.status);
  const Icon = getOnboardingStatusIcon(item.status);

  return (
    <Box display="flex" alignItems="center" gap="6px" flexWrap="wrap">
      <Icon sx={{ color: mappedStatus?.color, fontSize: 16 }} />
      <Typography component="span" sx={{ font: 'inherit', fontWeight: 600 }}>
        {mappedStatus?.label ?? item.status}
      </Typography>
      <Typography component="span" sx={{ font: 'inherit', whiteSpace: 'normal' }}>
        · Case {item.caseReferenceId ?? '—'} · Settlement #{item.settlementId}
      </Typography>
    </Box>
  );
};

const OnboardingStatusTooltipList = ({ items }: { items: ClientOnboardingStatusItem[] }) => (
  <Box display="flex" flexDirection="column" gap="6px">
    {items.map((item) => (
      <OnboardingStatusTooltipRow key={item.onboardingId} item={item} />
    ))}
  </Box>
);

const OnboardingStatusStack = ({
  status,
  items = [],
  fontSize = 20,
  maxVisible,
}: OnboardingStatusStackProps) => {
  const mappedStatus = getMappedStatus(status);

  if (!mappedStatus) {
    return null;
  }

  const visibleItems = maxVisible === undefined ? items : items.slice(0, maxVisible);
  const hiddenItems = items.slice(visibleItems.length);

  return (
    <Box
      component="span"
      onClick={(event) => event.stopPropagation()}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '4px',
        verticalAlign: 'middle',
        cursor: 'default',
      }}
    >
      {visibleItems.length ? (
        visibleItems.map((item, index) => {
          const Icon = getOnboardingStatusIcon(item.status);
          // The last visible icon carries the hidden count as a superscript badge.
          const showBadge = hiddenItems.length > 0 && index === visibleItems.length - 1;
          return (
            <StatusTooltip
              key={item.onboardingId}
              title={
                showBadge ? (
                  <OnboardingStatusTooltipList items={[item, ...hiddenItems]} />
                ) : (
                  <OnboardingStatusTooltipRow item={item} />
                )
              }
            >
              <Box
                component="span"
                sx={{
                  position: 'relative',
                  display: 'inline-flex',
                  mr: showBadge ? '10px' : 0,
                }}
              >
                <Icon sx={{ color: getMappedStatus(item.status)?.color, fontSize }} />
                {showBadge && (
                  <Box
                    component="span"
                    sx={(theme) => ({
                      position: 'absolute',
                      top: '-6px',
                      right: '-10px',
                      minWidth: '16px',
                      height: '16px',
                      px: '4px',
                      boxSizing: 'border-box',
                      borderRadius: '8px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.palette.info.main,
                      color: theme.palette.common.white,
                      fontFamily: 'Inter',
                      fontSize: '10px',
                      fontWeight: 600,
                      lineHeight: 1,
                    })}
                  >
                    {hiddenItems.length}
                  </Box>
                )}
              </Box>
            </StatusTooltip>
          );
        })
      ) : (
        <StatusTooltip title={mappedStatus.label}>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            {(() => {
              const Icon = getOnboardingStatusIcon(status || 'IN_PROGRESS');
              return <Icon sx={{ color: mappedStatus.color, fontSize }} />;
            })()}
          </Box>
        </StatusTooltip>
      )}
    </Box>
  );
};

export default OnboardingStatusStack;
